import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LanguageSelector } from '@/components/LanguageSelector';
import { AssistantPanel } from '@/components/assistant/AssistantPanel';
import { AssistantIntroBanner } from '@/components/assistant/AssistantIntroBanner';
import { useRecursoAtivo } from '@/hooks/useFeatureFlag';
import { useAssistantStore } from '@/stores/assistant';
import { useAuthStore } from '@/stores/auth';
import { XtreedLogo } from '@/components/ui/XtreedLogo';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  ArrowLeft,
  Package,
  Shield,
  Layers,
  Film,
  Trophy,
  Tag,
  Award,
  ClipboardList,
  ClipboardCheck,
  DollarSign,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLogout } from '@/hooks/useAuth';
import { GamifiedAvatar } from '@/components/ui/GamifiedAvatar';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@xtreed/academy-shared/ui';

interface NavItem {
  nameKey: ParseKeys<'layout'>;
  href: string;
  icon: LucideIcon;
  exact?: boolean;
  superAdminOnly?: boolean;
}

interface NavGroup {
  labelKey: ParseKeys<'layout'>;
  items: NavItem[];
}

const navigationGroups: NavGroup[] = [
  {
    labelKey: 'admin.groups.overview',
    items: [
      {
        nameKey: 'admin.nav.dashboard',
        href: '/admin',
        icon: LayoutDashboard,
        exact: true,
      },
    ],
  },
  {
    labelKey: 'admin.groups.content',
    items: [
      { nameKey: 'admin.nav.courses', href: '/admin/courses', icon: BookOpen },
      { nameKey: 'admin.nav.modules', href: '/admin/modules', icon: Layers },
      { nameKey: 'admin.nav.videos', href: '/admin/videos', icon: Film },
      {
        nameKey: 'admin.nav.categories',
        href: '/admin/categories',
        icon: Tag,
      },
      {
        nameKey: 'admin.nav.certificates',
        href: '/admin/certificates',
        icon: Award,
      },
    ],
  },
  {
    labelKey: 'admin.groups.commercial',
    items: [
      {
        nameKey: 'admin.nav.accessGroups',
        href: '/admin/access-groups',
        icon: Package,
      },
    ],
  },
  {
    labelKey: 'admin.groups.community',
    items: [
      { nameKey: 'admin.nav.members', href: '/admin/users', icon: Users },
      {
        nameKey: 'admin.nav.customFields',
        href: '/admin/custom-fields',
        icon: ClipboardList,
      },
      {
        nameKey: 'admin.nav.surveys',
        href: '/admin/surveys',
        icon: ClipboardCheck,
      },
      {
        nameKey: 'admin.nav.gamification',
        href: '/admin/gamification',
        icon: Trophy,
      },
    ],
  },
  {
    labelKey: 'admin.groups.platform',
    items: [
      {
        nameKey: 'admin.nav.videoUsage',
        href: '/admin/video-usage',
        icon: DollarSign,
        superAdminOnly: true,
      },
      {
        nameKey: 'admin.nav.settings',
        href: '/admin/settings',
        icon: Settings,
      },
    ],
  },
];

export function AdminLayout() {
  const { t } = useTranslation('layout');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('admin-sidebar-open');
    return stored === null ? true : stored === 'true';
  });
  const location = useLocation();
  const { user } = useAuthStore();
  const { mutate: logout } = useLogout();
  const assistantEnabled = useRecursoAtivo('assistant');
  const assistantOpen = useAssistantStore((state) => state.isOpen);
  const toggleAssistant = useAssistantStore((state) => state.toggle);
  const isHideAssistantBanner = useAssistantStore(
    (state) => state.isHideAssistantBanner,
  );

  const visibleGroups = navigationGroups.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.superAdminOnly || user?.role === 'SUPER_ADMIN',
    ),
  }));

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem('admin-sidebar-open', String(next));
      return next;
    });
  }, []);

  // Helper: check if a nav item matches the current route
  const isActive = (item: NavItem) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    return location.pathname.startsWith(item.href);
  };

  // Helper: check if any item in a group matches the current route
  const isGroupActive = (group: NavGroup) => {
    return group.items.some((item) => isActive(item));
  };

  // Find the current page name for the breadcrumb
  const currentPageName = (() => {
    for (const group of visibleGroups) {
      for (const item of group.items) {
        if (
          item.exact
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href)
        ) {
          // Don't show breadcrumb segment for the dashboard root
          if (item.exact && item.href === '/admin') return null;
          return t(item.nameKey);
        }
      }
    }
    return null;
  })();

  // Força dark mode no admin
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 animate-fade-in bg-black/60 backdrop-blur-xs lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[260px] transform flex-col border-r backdrop-blur-xl transition-all duration-300 ease-smooth',
          'border-border/50 bg-card/95',
          // Mobile: slide in/out
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Desktop: full width when open, rail when closed
          desktopSidebarOpen ? 'lg:w-[260px]' : 'lg:w-[72px]',
        )}
      >
        {/* Gradient line at top */}
        <div className="absolute top-0 right-0 left-0 h-[2px] bg-linear-to-r from-primary via-xtreed-green-light to-primary" />

        {/* Sidebar Header */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center justify-between gap-2 border-b border-border/50 pr-2 pl-4',
            !desktopSidebarOpen && 'lg:justify-center lg:px-0',
          )}
        >
          <Link
            to="/admin"
            className={cn(
              'flex min-w-0 items-center gap-2',
              !desktopSidebarOpen && 'lg:hidden',
            )}
          >
            <XtreedLogo size="sm" showText />
            <span className="rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-primary-ink uppercase">
              {t('admin.badge')}
            </span>
          </Link>
          {/* Logo only (rail mode, desktop) */}
          {!desktopSidebarOpen && (
            <Link
              to="/admin"
              className="hidden items-center justify-center lg:flex"
              title={t('admin.nav.dashboard')}
            >
              <XtreedLogo size="sm" showText={false} />
            </Link>
          )}
          {/* Mobile close */}
          <button
            className="shrink-0 rounded-lg p-3 transition-colors hover:bg-secondary/80 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            title={t('admin.closeMenu')}
          >
            <X className="h-5 w-5" />
          </button>
          {/* Desktop close (only when expanded) */}
          {desktopSidebarOpen && (
            <button
              className="hidden shrink-0 rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:block"
              onClick={toggleDesktopSidebar}
              title={t('admin.collapseMenu')}
            >
              <PanelLeftClose className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Desktop expand button (rail mode) */}
        {!desktopSidebarOpen && (
          <button
            onClick={toggleDesktopSidebar}
            title={t('admin.expandMenu')}
            className="mx-2 mt-2 hidden h-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground lg:flex"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}

        {/* Navigation - scrollable area */}
        <nav
          className={cn(
            'flex-1 overflow-x-hidden overflow-y-auto py-3',
            desktopSidebarOpen ? 'px-3' : 'px-3 lg:px-2',
          )}
        >
          {visibleGroups.map((group) => {
            const groupActive = isGroupActive(group);

            return (
              <div key={group.labelKey} className="mb-1">
                {/* Group Header — hidden in rail mode */}
                <div
                  className={cn(
                    'flex w-full items-center rounded-lg px-2.5 py-1.5 text-[10px] font-semibold tracking-wider uppercase',
                    groupActive
                      ? 'text-primary-ink/80'
                      : 'text-muted-foreground/60',
                    !desktopSidebarOpen && 'lg:hidden',
                  )}
                >
                  <span>{t(group.labelKey)}</span>
                </div>
                {/* Divider in rail mode */}
                {!desktopSidebarOpen && (
                  <div className="mx-auto my-2 hidden h-px w-6 bg-border/40 lg:block" />
                )}

                {/* Group Items */}
                <div className="mt-0.5 space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item);
                    return (
                      <Link
                        key={item.nameKey}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        title={
                          !desktopSidebarOpen ? t(item.nameKey) : undefined
                        }
                        className={cn(
                          'group relative flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200',
                          !desktopSidebarOpen &&
                            'lg:h-10 lg:justify-center lg:gap-0 lg:px-0 lg:py-0',
                          active
                            ? 'bg-primary/10 text-primary-ink'
                            : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                        )}
                      >
                        {active && !desktopSidebarOpen && (
                          <div className="absolute left-0 hidden h-5 w-0.5 rounded-r-full bg-primary lg:block" />
                        )}
                        <item.icon
                          className={cn(
                            'h-4 w-4 shrink-0 transition-colors',
                            active
                              ? 'text-primary-ink'
                              : 'group-hover:text-foreground',
                          )}
                        />
                        <span
                          className={cn(
                            'flex-1 truncate',
                            !desktopSidebarOpen && 'lg:hidden',
                          )}
                        >
                          {t(item.nameKey)}
                        </span>
                        {active && (
                          <div
                            className={cn(
                              'h-1.5 w-1.5 rounded-full bg-primary',
                              !desktopSidebarOpen && 'lg:hidden',
                            )}
                          />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div
          className={cn(
            'shrink-0 space-y-0.5 border-t border-border/50 p-3',
            !desktopSidebarOpen && 'lg:p-2',
          )}
        >
          <Link
            to="/dashboard"
            onClick={() => setSidebarOpen(false)}
            title={!desktopSidebarOpen ? t('admin.backToApp') : undefined}
            className={cn(
              'group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground transition-all hover:bg-primary/5 hover:text-primary-ink',
              !desktopSidebarOpen &&
                'lg:h-10 lg:justify-center lg:gap-0 lg:px-0 lg:py-0',
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span className={cn(!desktopSidebarOpen && 'lg:hidden')}>
              {t('admin.backToApp')}
            </span>
          </Link>
          <button
            onClick={() => logout()}
            title={!desktopSidebarOpen ? t('admin.logout') : undefined}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold text-destructive transition-all hover:bg-destructive/10',
              !desktopSidebarOpen &&
                'lg:h-10 lg:justify-center lg:gap-0 lg:px-0 lg:py-0',
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={cn(!desktopSidebarOpen && 'lg:hidden')}>
              {t('admin.logout')}
            </span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          'flex min-h-screen overflow-x-clip transition-[padding] duration-300',
          desktopSidebarOpen ? 'lg:pl-[260px]' : 'lg:pl-[72px]',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col overflow-x-clip">
          {/* Header */}
          <header className="sticky top-0 z-30 h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl">
            <div className="flex h-full items-center justify-between px-3 sm:px-4 lg:px-6">
              <div className="flex min-w-0 items-center gap-3">
                {/* Mobile menu button */}
                <button
                  className="rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:hidden"
                  onClick={() => setSidebarOpen(true)}
                  title={t('admin.openMenu')}
                >
                  <Menu className="h-5 w-5" />
                </button>
                {/* Breadcrumb-style page indicator */}
                <div className="hidden min-w-0 items-center gap-2 whitespace-nowrap sm:flex">
                  <Shield className="h-4 w-4 shrink-0 text-primary-ink" />
                  <span
                    className={cn(
                      'text-sm text-muted-foreground',
                      assistantOpen && 'hidden xl:inline',
                    )}
                  >
                    {t('admin.panelTitle')}
                  </span>
                  {currentPageName && (
                    <>
                      <span
                        className={cn(
                          'text-sm text-muted-foreground/40',
                          assistantOpen && 'hidden xl:inline',
                        )}
                      >
                        /
                      </span>
                      <span className="truncate text-sm font-medium text-foreground">
                        {currentPageName}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                {assistantEnabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAssistant}
                    aria-pressed={assistantOpen}
                    title={
                      assistantOpen
                        ? t('admin.assistant.close')
                        : t('admin.assistant.open')
                    }
                    className={cn(
                      'gap-2 rounded-xl px-2.5',
                      assistantOpen
                        ? 'bg-primary/10 text-primary-ink hover:bg-primary/15'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <span className="relative flex">
                      {!isHideAssistantBanner && !assistantOpen && (
                        <span className="absolute inset-0 animate-ping rounded-full bg-primary/50" />
                      )}
                      <Sparkles className="relative h-4 w-4" />
                    </span>
                    {!assistantOpen && (
                      <span className="hidden text-sm md:inline">
                        {t('admin.assistant.open')}
                      </span>
                    )}
                  </Button>
                )}
                <LanguageSelector />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-secondary/80"
                    >
                      <GamifiedAvatar
                        src={user?.avatar}
                        name={user?.name}
                        size="sm"
                      />
                      <div
                        className={cn(
                          'hidden text-left sm:block',
                          assistantOpen && 'sm:hidden 2xl:block',
                        )}
                      >
                        <p className="text-sm leading-none font-medium">
                          {user?.name}
                        </p>
                        <p className="mt-0.5 text-[10px] font-semibold tracking-wider text-primary-ink uppercase">
                          {user?.role}
                        </p>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-60 rounded-xl border border-border/50 bg-card/95 p-1 shadow-xl backdrop-blur-xl"
                  >
                    <div className="mb-1 rounded-lg bg-linear-to-r from-primary/10 to-primary/5 px-3 py-3">
                      <p className="truncate font-semibold">{user?.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/dashboard"
                        className="cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-muted focus:bg-muted focus:text-foreground"
                      >
                        <ArrowLeft className="mr-3 h-4 w-4 text-primary-ink" />
                        {t('admin.backToApp')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/admin/settings"
                        className="cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-muted focus:bg-muted focus:text-foreground"
                      >
                        <Settings className="mr-3 h-4 w-4 text-primary-ink" />
                        {t('admin.nav.settings')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-1 bg-border/50" />
                    <DropdownMenuItem
                      onClick={() => logout()}
                      className="cursor-pointer rounded-lg px-3 py-2.5 text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:text-destructive"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      {t('admin.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 p-4 md:p-5 lg:p-6">
            <div className="animate-fade-in">
              <Outlet />
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-border/50 bg-card/30 px-3 py-3 sm:px-4 lg:px-6">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                &copy; {new Date().getFullYear()} Xtreed
                <span className="text-muted-foreground/40">·</span>
                <span className="flex items-center gap-1 font-medium text-primary-ink">
                  <XtreedLogo size="sm" showText={false} />
                  Xtreed
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
                <span>{t('admin.online')}</span>
              </div>
            </div>
          </footer>
        </div>

        {assistantEnabled && <AssistantPanel />}
        {assistantEnabled && <AssistantIntroBanner />}
      </div>
    </div>
  );
}
